import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getNewbuildingsSummary, getNewbuildingsSummarySafe } from '../../actions/newbuildingsSummary';
import { AiSummaryModalMobile, type IAiSummaryMobileActions } from '../../components/AiSummaryModalMobile';
import { selectNewbuildingsSummary } from '../../selectors/newbuildingsSummary';
import type { TThunkDispatch } from '../../types/redux/thunk';
import { NewbuildingCallToAction } from '../NewbuildingCallToAction';
import { NewbuildingChatButton } from '../NewbuildingChatButton';

export interface IAiSummaryModalMobileContainerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const AiSummaryModalMobileContainer = ({
  open,
  onOpenChange,
}: IAiSummaryModalMobileContainerProps): React.JSX.Element => {
  const dispatch = useDispatch<TThunkDispatch>();
  const { data: summary, requestId, status } = useSelector(selectNewbuildingsSummary);
  const handleClose = React.useCallback(() => onOpenChange(false), [onOpenChange]);
  const handleReload = React.useCallback(() => dispatch(getNewbuildingsSummary()), [dispatch]);

  React.useEffect(() => {
    if (open) {
      dispatch(getNewbuildingsSummarySafe());
    }
  }, [dispatch, open]);

  const actions = React.useMemo<IAiSummaryMobileActions[]>(
    () =>
      (summary?.summarization ?? []).map(newbuilding => {
        const hasChat = [...newbuilding.builders, ...newbuilding.sellers].some(contact => contact.chat?.enabled);

        return {
          newbuildingId: newbuilding.id,
          call: (
            <NewbuildingCallToAction
              onlyIcon
              builders={newbuilding.builders}
              newbuildingId={newbuilding.id}
              sellers={newbuilding.sellers}
              url={newbuilding.fullUrl}
            />
          ),
          chat: hasChat ? (
            <NewbuildingChatButton
              builders={newbuilding.builders}
              fullscreen
              newbuildingId={newbuilding.id}
              sellers={newbuilding.sellers}
              size="XS"
              url={newbuilding.fullUrl}
            />
          ) : undefined,
        };
      }),
    [summary],
  );

  return (
    <AiSummaryModalMobile
      actions={actions}
      onClose={handleClose}
      onReload={handleReload}
      open={open}
      requestId={requestId}
      status={status}
      summary={summary}
    />
  );
};
